import React from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import Mark from "./Mark";
import Tape from "./Tape";
import WalletModal from "./WalletModal";
import { useWallet } from "../context/WalletContext";
import { PUBLIC, CHAIN_ID, DOMAIN } from "../config";
import useLiveBoard from "../hooks/useLiveBoard";

const links = [
  { to: "/listed", label: "Listed" },
  { to: "/pairs",  label: "Pairs"  },
  { to: "/new",    label: "New"    },
  { to: "/book",   label: "Book"   },
  { to: "/docs",   label: "Docs"   },
];

function HeaderTape({ rows }) {
  const nvda = rows.find((r) => r.ticker === "NVDA");
  if (!nvda) return <div />;
  const hasBoth = nvda.pool != null && nvda.nav != null;
  const delta = hasBoth ? ((nvda.pool - nvda.nav) / nvda.nav) * 100 : null;
  const dCls = delta == null ? "" : delta > 0 ? "up" : delta < 0 ? "down" : "";
  const fmt = (v) => (v == null ? "—" : Number(v).toFixed(2));
  return (
    <div
      className="tabular"
      data-testid="header-tape"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 18,
        fontSize: 11,
        letterSpacing: "0.14em",
        color: "var(--ink-muted)",
      }}
    >
      <span>
        <span>NVDA POOL </span>
        <span style={{ color: "var(--ink)" }}>{fmt(nvda.pool)}</span>
      </span>
      <span style={{ opacity: 0.5 }}>·</span>
      <span>
        <span>NAV </span>
        <span style={{ color: "var(--ink)" }}>{fmt(nvda.nav)}</span>
      </span>
      <span style={{ opacity: 0.5 }}>·</span>
      <span className={dCls} style={{ color: dCls ? undefined : "var(--ink)" }}>
        {delta == null ? "—" : (delta === 0 ? "±0.00%" : `${delta > 0 ? "+" : "−"}${Math.abs(delta).toFixed(2)}%`)}
      </span>
      <span style={{ opacity: 0.5 }}>·</span>
      <span>SESSION <span style={{ color: "var(--ink)" }}>{nvda.session}</span></span>
    </div>
  );
}

export default function Layout({ children }) {
  const rows = useLiveBoard(1400);
  const { wallet, setOpen, disconnect, wrongChain, switchChain, switching } = useWallet();
  const location = useLocation();

  return (
    <div style={{ minHeight: "100vh", background: "var(--paper)", color: "var(--ink)" }}>
      {/* Header — 52px */}
      <header
        className="nav-header"
        style={{
          height: 52,
          borderBottom: "1px solid var(--line)",
          background: "var(--paper)",
          position: "sticky",
          top: 0,
          zIndex: 40,
        }}
      >
        <div
          className="mx-auto nav-header-grid"
          style={{
            maxWidth: 1520,
            height: "100%",
            padding: "0 28px",
            display: "grid",
            gridTemplateColumns: "180px 1fr auto auto",
            alignItems: "center",
            gap: 24,
          }}
        >
          <Link
            to="/"
            style={{ textDecoration: "none", color: "var(--ink)", display: "flex", alignItems: "center", gap: 8 }}
            data-testid="header-brand"
          >
            <Mark size={20} />
          </Link>

          <nav className="nav-header-nav" style={{ display: "flex", gap: 24 }} aria-label="Primary">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                data-testid={`nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="nav-header-tape">
            <HeaderTape rows={rows} />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: "flex-end" }}>
            {wallet && wrongChain && (
              <button
                onClick={switchChain}
                data-testid="switch-chain-btn"
                disabled={switching}
                style={{
                  padding: "6px 10px",
                  fontSize: 11,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "var(--down)",
                  background: "transparent",
                  border: "1px solid var(--down)",
                  cursor: "pointer",
                }}
              >
                {switching ? "Switching…" : `Switch to ${CHAIN_ID}`}
              </button>
            )}

            {wallet ? (
              <button
                className="btn-outline"
                style={{ padding: "6px 12px", fontSize: 12 }}
                onClick={disconnect}
                data-testid="wallet-connected"
                title={`Chain ${wallet.chainId}`}
              >
                {wallet.address}
              </button>
            ) : (
              <button
                className="btn-outline"
                style={{ padding: "6px 14px", fontSize: 12 }}
                onClick={() => setOpen(true)}
                data-testid="connect-btn"
              >
                Connect
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Tape */}
      <Tape rows={rows} />

      {/* Page */}
      <main key={location.pathname} className="page-enter">
        {typeof children === "function" ? children({ rows }) : children}
      </main>

      {/* Footer */}
      <footer
        style={{
          borderTop: "1px solid var(--line)",
          marginTop: 96,
          padding: "36px 28px 56px",
        }}
      >
        <div
          className="mx-auto"
          style={{
            maxWidth: 1520,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 24,
            flexWrap: "wrap",
          }}
        >
          <div className="serif" style={{ fontSize: 22 }}>Listed names. Live NAV.</div>
          <div style={{ color: "var(--ink-muted)", fontSize: 12.5, letterSpacing: "0.02em" }}>
            {DOMAIN} · Read from Robinhood Chain.
          </div>
        </div>
      </footer>

      <WalletModal />
    </div>
  );
}
